import React, {
  useState,
  useEffect,
  useReducer,
  useCallback,
  useContext,
} from "react";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import IconButton from "@material-ui/core/IconButton";
import SearchIcon from "@material-ui/icons/Search";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import CalendarTodayIcon from "@material-ui/icons/CalendarToday";
import ViewListIcon from "@material-ui/icons/ViewList";
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import esLocale from '@fullcalendar/core/locales/es';
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import ScheduleModal from "../../components/ScheduleModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import moment from "moment";
import { capitalize } from "lodash";
import { AuthContext } from "../../context/Auth/AuthContext";
import usePlans from "../../hooks/usePlans";
import { SocketContext } from "../../context/Socket/SocketContext";

// A custom hook that builds on useLocation to parse
// the query string for you.
const getUrlParam = (param) => {
  return new URLSearchParams(window.location.search).get(param);
};

const reducer = (state, action) => {
  if (action.type === "LOAD_SCHEDULES") {
    const schedules = action.payload;
    const newSchedules = [];

    schedules.forEach((schedule) => {
      const scheduleIndex = state.findIndex((s) => s.id === schedule.id);
      if (scheduleIndex !== -1) {
        state[scheduleIndex] = schedule;
      } else {
        newSchedules.push(schedule);
      }
    });

    return [...state, ...newSchedules];
  }

  if (action.type === "UPDATE_SCHEDULES") {
    const schedule = action.payload;
    const scheduleIndex = state.findIndex((s) => s.id === schedule.id);

    if (scheduleIndex !== -1) {
      state[scheduleIndex] = schedule;
      return [...state];
    } else {
      return [schedule, ...state];
    }
  }

  if (action.type === "DELETE_SCHEDULE") {
    const scheduleId = action.payload;

    const scheduleIndex = state.findIndex((s) => s.id === scheduleId);
    if (scheduleIndex !== -1) {
      state.splice(scheduleIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(3),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
    boxShadow: "0 2px 20px rgba(0,0,0,0.08)",
    borderRadius: "24px",
    background: theme.palette.background.paper,
    position: "relative",
    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      height: "8px",
      background: "linear-gradient(90deg, #2196F3 0%, #4CAF50 100%)",
      borderRadius: "24px 24px 0 0",
    }
  },
  searchContainer: {
    display: "flex",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(3),
    padding: theme.spacing(3),
    background: "linear-gradient(180deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.6) 100%)",
    backdropFilter: "blur(8px)",
    borderRadius: "16px",
    boxShadow: "0 4px 15px rgba(0,0,0,0.05)",
    border: "1px solid rgba(255,255,255,0.8)",
  },
  searchInput: {
    flex: 1,
    "& .MuiOutlinedInput-root": {
      borderRadius: "12px",
      background: "rgba(255,255,255,0.9)",
      backdropFilter: "blur(4px)",
      transition: "all 0.3s ease",
      "& fieldset": {
        borderColor: "rgba(0, 0, 0, 0.1)",
        transition: "all 0.3s ease",
      },
      "&:hover fieldset": {
        borderColor: theme.palette.primary.main,
      },
      "&.Mui-focused": {
        boxShadow: "0 0 0 3px rgba(33,150,243,0.15)",
      }
    },
    "& .MuiOutlinedInput-input": {
      padding: "12px 14px",
    },
  },
  addButton: {
    borderRadius: "12px",
    padding: "10px 24px",
    background: "linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)",
    boxShadow: "0 3px 12px rgba(33,150,243,0.3)",
    fontSize: "14px",
    fontWeight: "600",
    textTransform: "none",
    transition: "all 0.3s ease",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 5px 15px rgba(33,150,243,0.4)",
    },
    "&:active": {
      transform: "translateY(0)",
    }
  },
  tableContainer: {
    borderRadius: "16px",
    background: "rgba(255,255,255,0.8)",
    backdropFilter: "blur(8px)",
    overflow: "hidden",
    boxShadow: "0 4px 15px rgba(0,0,0,0.05)",
    border: "1px solid rgba(255,255,255,0.8)",
  },
  customTableCell: {
    borderBottom: "1px solid rgba(224, 224, 224, 0.2)",
    padding: theme.spacing(2),
    fontSize: "14px",
    color: theme.palette.text.secondary,
    transition: "all 0.3s ease",
  },
  tableHeader: {
    background: "linear-gradient(180deg, rgba(33,150,243,0.05) 0%, rgba(33,150,243,0.02) 100%)",
    backdropFilter: "blur(4px)",
    "& .MuiTableCell-head": {
      color: theme.palette.primary.main,
      fontWeight: 600,
      fontSize: "14px",
      lineHeight: "1.5",
      letterSpacing: "0.5px",
      textTransform: "uppercase",
    },
  },
  actionButtons: {
    display: "flex",
    gap: theme.spacing(1),
    justifyContent: "center",
    opacity: 0,
    transition: "all 0.3s ease",
  },
  iconButton: {
    padding: "8px",
    borderRadius: "10px",
    color: theme.palette.text.secondary,
    transition: "all 0.3s ease",
    background: "rgba(255,255,255,0.9)",
    backdropFilter: "blur(4px)",
    "&:hover": {
      background: theme.palette.primary.main,
      color: "#fff",
      transform: "translateY(-2px)",
      boxShadow: "0 3px 8px rgba(33,150,243,0.3)",
    },
    "&:active": {
      transform: "translateY(0)",
    }
  },
  statusCell: {
    "& span": {
      padding: "6px 16px",
      borderRadius: "20px",
      fontSize: "13px",
      fontWeight: "500",
      letterSpacing: "0.5px",
      textTransform: "uppercase",
      transition: "all 0.3s ease",
      boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
    },
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(6),
    background: "rgba(255,255,255,0.8)",
    backdropFilter: "blur(8px)",
    borderRadius: "16px",
  },
  noSchedulesContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: theme.spacing(8),
    flexDirection: "column",
    gap: theme.spacing(2),
    background: "rgba(255,255,255,0.8)",
    backdropFilter: "blur(8px)",
    borderRadius: "16px",
  },
  noSchedulesText: {
    color: theme.palette.text.secondary,
    textAlign: "center",
    fontSize: "15px",
    fontWeight: "500",
    letterSpacing: "0.5px",
  },
  tableRowHover: {
    transition: "all 0.3s ease",
    "&:hover": {
      backgroundColor: "rgba(33,150,243,0.05) !important",
      transform: "scale(1.002)",
      "& $actionButtons": {
        opacity: 1,
      }
    },
  },
  truncatedText: {
    maxWidth: "200px",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    margin: "0 auto",
    padding: "4px 12px",
    borderRadius: "8px",
    background: "rgba(0,0,0,0.02)",
    transition: "all 0.3s ease",
    "&:hover": {
      background: "rgba(33,150,243,0.05)",
      maxWidth: "300px",
    }
  },
  headerTitle: {
    fontSize: "24px",
    fontWeight: "600",
    background: "linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    marginBottom: theme.spacing(1),
  },
  viewSelector: {
    display: "flex",
    gap: theme.spacing(1),
    marginLeft: "auto",
  },
  viewButton: {
    padding: "8px 12px",
    borderRadius: "8px",
    color: theme.palette.text.secondary,
    backgroundColor: "transparent",
    border: "1px solid " + theme.palette.divider,
    "&.active": {
      backgroundColor: theme.palette.primary.main,
      color: "#fff",
      borderColor: theme.palette.primary.main,
    },
    "&:hover": {
      backgroundColor: theme.palette.primary.light,
      color: "#fff",
      borderColor: theme.palette.primary.light,
    },
  },
  calendarContainer: {
    padding: theme.spacing(2),
    background: "rgba(255,255,255,0.8)",
    backdropFilter: "blur(8px)",
    borderRadius: "16px",
    boxShadow: "0 4px 15px rgba(0,0,0,0.05)",
    border: "1px solid rgba(255,255,255,0.8)",
    "& .fc": {
      "--fc-border-color": "rgba(0,0,0,0.1)",
      "--fc-button-bg-color": theme.palette.primary.main,
      "--fc-button-border-color": theme.palette.primary.main,
      "--fc-button-hover-bg-color": theme.palette.primary.dark,
      "--fc-button-hover-border-color": theme.palette.primary.dark,
      "--fc-button-active-bg-color": theme.palette.primary.dark,
      "--fc-today-bg-color": "rgba(33,150,243,0.1)",
      "--fc-event-bg-color": theme.palette.primary.main,
      "--fc-event-border-color": theme.palette.primary.main,
    },
    "& .fc-toolbar-title": {
      fontSize: "1.2rem",
      fontWeight: 500,
      textTransform: "capitalize",
    },
    "& .fc-button": {
      textTransform: "capitalize",
      borderRadius: "8px",
    },
    "& .fc-event": {
      cursor: "pointer",
      borderRadius: "6px",
      padding: "2px 4px",
      fontSize: "0.85em",
      transition: "all 0.2s",
      "&:hover": {
        transform: "translateY(-1px)",
        boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
      },
    },
  },
}));

const Schedules = () => {
  const classes = useStyles();
  const history = useHistory();

  const { user } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [deletingSchedule, setDeletingSchedule] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [schedules, dispatch] = useReducer(reducer, []);
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [contactId, setContactId] = useState(+getUrlParam("contactId"));
  const socketManager = useContext(SocketContext);
  const { getPlanCompany } = usePlans();
  const [viewMode, setViewMode] = useState("list");

  useEffect(() => {
    async function fetchData() {
      const planConfigs = await getPlanCompany(undefined, user.companyId);
      if (!planConfigs.plan.useSchedules) {
        toast.error("Esta empresa no tiene permiso para acceder a esta página! Te estamos redirigiendo.");
        setTimeout(() => {
          history.push(`/`)
        }, 1000);
      }
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const fetchSchedules = useCallback(async () => {
    try {
      const { data } = await api.get("/schedules/", {
        params: { searchParam, pageNumber },
      });
      dispatch({ type: "LOAD_SCHEDULES", payload: data.schedules });
      setHasMore(data.hasMore);
      setLoading(false);
    } catch (err) {
      toastError(err);
    }
  }, [searchParam, pageNumber]);

  const handleOpenScheduleModalFromContactId = useCallback(() => {
    if (contactId) {
      handleOpenScheduleModal();
    }
  }, [contactId]);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      fetchSchedules();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [
    searchParam,
    pageNumber,
    contactId,
    fetchSchedules,
    handleOpenScheduleModalFromContactId,
  ]);

  useEffect(() => {
    handleOpenScheduleModalFromContactId();
    const socket = socketManager.getSocket(user.companyId);
    const onCompanyUser = (data) => {
			if (data.action === "update" || data.action === "create") {
				dispatch({ type: "UPDATE_SCHEDULES", payload: data.schedules });
			}

			if (data.action === "delete") {
				dispatch({ type: "DELETE_USER", payload: +data.scheduleId });
			}
    };

    socket.on(`company-${user.companyId}-user`, onCompanyUser);

    return () => {
      socket.off(`company-${user.companyId}-user`, onCompanyUser);
    };
  }, [handleOpenScheduleModalFromContactId, user, socketManager]);

  const cleanContact = () => {
    setContactId("");
  };

  const handleOpenScheduleModal = () => {
    setSelectedSchedule(null);
    setScheduleModalOpen(true);
  };

  const handleCloseScheduleModal = () => {
    setSelectedSchedule(null);
    setScheduleModalOpen(false);
  };

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleEditSchedule = (schedule) => {
    setSelectedSchedule(schedule);
    setScheduleModalOpen(true);
  };

  const handleDeleteSchedule = async (scheduleId) => {
    try {
      await api.delete(`/schedules/${scheduleId}`);
      toast.success("Programación eliminada exitosamente");
    } catch (err) {
      toastError(err);
    }
    setDeletingSchedule(null);
    setSearchParam("");
    setPageNumber(1);

    dispatch({ type: "RESET" });
    setPageNumber(1);
    await fetchSchedules();
  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  const truncate = (str, len) => {
    if (str.length > len) {
      return str.substring(0, len) + "...";
    }
    return str;
  };

  const formatEventsForCalendar = (schedules) => {
    return schedules.map(schedule => ({
      id: schedule.id,
      title: `${schedule.contact.name} - ${truncate(schedule.body, 15)}`,
      start: schedule.sendAt,
      backgroundColor: schedule.status === "PENDENTE" ? "#FFA726" : 
                      schedule.status === "ENVIADO" ? "#4CAF50" : 
                      schedule.status === "ERRO" ? "#E53935" : "#78909C",
      borderColor: "transparent",
      textColor: "#fff",
      extendedProps: {
        schedule: schedule
      }
    }));
  };

  const handleEventClick = (eventInfo) => {
    const schedule = eventInfo.event.extendedProps.schedule;
    handleEditSchedule(schedule);
  };

  return (
    <MainContainer>
      <MainHeader>
        <div>
          <h1 className={classes.headerTitle}>Programaciones</h1>
        </div>
        <MainHeaderButtonsWrapper>
          <div className={classes.viewSelector}>
            <Button
              variant="outlined"
              className={`${classes.viewButton} ${viewMode === 'list' ? 'active' : ''}`}
              onClick={() => setViewMode('list')}
              startIcon={<ViewListIcon />}
            >
              Lista
            </Button>
            <Button
              variant="outlined"
              className={`${classes.viewButton} ${viewMode === 'calendar' ? 'active' : ''}`}
              onClick={() => setViewMode('calendar')}
              startIcon={<CalendarTodayIcon />}
            >
              Calendario
            </Button>
          </div>
          <Button
            variant="contained"
            color="primary"
            onClick={handleOpenScheduleModal}
            className={classes.addButton}
            startIcon={<i className="fas fa-plus" style={{ fontSize: "14px" }} />}
          >
            Agregar
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>
      <Paper className={classes.mainPaper} onScroll={handleScroll}>
        <div className={classes.searchContainer}>
          <TextField
            placeholder="Buscar programación..."
            type="search"
            value={searchParam}
            onChange={handleSearch}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon style={{ color: 'rgba(33,150,243,0.8)', fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
            className={classes.searchInput}
            variant="outlined"
            size="small"
            fullWidth
          />
        </div>
        
        {viewMode === 'list' ? (
          <div className={classes.tableContainer}>
            <Table size="small">
              <TableHead className={classes.tableHeader}>
                <TableRow>
                  <TableCell className={classes.customTableCell}>Contacto</TableCell>
                  <TableCell className={classes.customTableCell} align="center">Mensaje</TableCell>
                  <TableCell className={classes.customTableCell} align="center">Enviar en</TableCell>
                  <TableCell className={classes.customTableCell} align="center">Estado</TableCell>
                  <TableCell className={classes.customTableCell} align="center">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRowSkeleton />
                ) : (
                  <>
                    {schedules.length > 0 &&
                      schedules.map((schedule) => (
                        <TableRow key={schedule.id} className={classes.tableRowHover}>
                          <TableCell className={classes.customTableCell}>
                            {schedule.contact.name}
                          </TableCell>
                          <TableCell className={classes.customTableCell} align="center">
                            <div className={classes.truncatedText} title={schedule.body}>
                              {truncate(schedule.body, 25)}
                            </div>
                          </TableCell>
                          <TableCell className={classes.customTableCell} align="center">
                            {moment(schedule.sendAt).format("DD/MM/YYYY HH:mm")}
                          </TableCell>
                          <TableCell className={`${classes.customTableCell} ${classes.statusCell}`} align="center">
                            <span style={{
                              background: schedule.status === "PENDENTE" ? "linear-gradient(45deg, #FFB74D 0%, #FFA726 100%)" : 
                                        schedule.status === "ENVIADO" ? "linear-gradient(45deg, #66BB6A 0%, #4CAF50 100%)" : 
                                        schedule.status === "ERRO" ? "linear-gradient(45deg, #EF5350 0%, #E53935 100%)" : 
                                        "linear-gradient(45deg, #90A4AE 0%, #78909C 100%)",
                              color: "#fff",
                              textShadow: "0 1px 2px rgba(0,0,0,0.2)"
                            }}>
                              {capitalize(schedule.status)}
                            </span>
                          </TableCell>
                          <TableCell className={classes.customTableCell} align="center">
                            <div className={classes.actionButtons}>
                              <IconButton
                                size="small"
                                onClick={() => handleEditSchedule(schedule)}
                                className={classes.iconButton}
                                title="Editar"
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setConfirmModalOpen(true);
                                  setDeletingSchedule(schedule);
                                }}
                                className={classes.iconButton}
                                title="Eliminar"
                              >
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    {schedules.length === 0 && !loading && (
                      <TableRow>
                        <TableCell colSpan={5}>
                          <div className={classes.noSchedulesContainer}>
                            <span className={classes.noSchedulesText}>
                              No hay programaciones
                            </span>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </>
                )}
              </TableBody>
            </Table>
          </div>
        ) : (
          <div className={classes.calendarContainer}>
            <FullCalendar
              plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
              initialView="dayGridMonth"
              headerToolbar={{
                left: 'prev,next today',
                center: 'title',
                right: 'dayGridMonth,timeGridWeek,timeGridDay'
              }}
              locale={esLocale}
              events={formatEventsForCalendar(schedules)}
              eventClick={handleEventClick}
              height="auto"
              eventTimeFormat={{
                hour: '2-digit',
                minute: '2-digit',
                hour12: false
              }}
              slotMinTime="06:00:00"
              slotMaxTime="23:00:00"
              allDaySlot={false}
              buttonText={{
                today: 'Hoy',
                month: 'Mes',
                week: 'Semana',
                day: 'Día'
              }}
            />
          </div>
        )}
      </Paper>
      <ScheduleModal
        open={scheduleModalOpen}
        onClose={handleCloseScheduleModal}
        reload={fetchSchedules}
        scheduleId={selectedSchedule?.id}
        contactId={contactId}
        cleanContact={cleanContact}
      />
      <ConfirmationModal
        title="¿Eliminar programación?"
        open={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        onConfirm={() => handleDeleteSchedule(deletingSchedule.id)}
      >
        ¿Está seguro? Esta acción no se puede deshacer.
      </ConfirmationModal>
    </MainContainer>
  );
};

export default Schedules;
